// React Query hooks for the Wave D QuickBooks integration.

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from '../../../../../api/client';
import { endpoints } from '../../../../../api/endpoints';
import { queryKeys } from '../../../../../api/queryKeys';
import { useToast } from '../../../../../hooks/useToast';
import type {
  QbAccountSummary,
  QbValidateResult,
  QbAuthInitiateResponse,
  QbEligibleResponse,
  QbGroupSyncBody,
  QbGroupSyncResult,
  QbSyncRecordsResponse,
  QbMappingDto,
  QbMappingKind,
  QbMappingUpsertBody,
  QbMappingsResponse,
  QbModule,
  QbSettingsPatch,
  QbSettingsResponse,
  QbStatusResponse,
  QbSyncListResponse,
  QbSyncTriggerResponse,
} from '../../../../../types/api';

export function useQbStatus(module: QbModule) {
  return useQuery<QbStatusResponse>({
    queryKey: queryKeys.quickbooks.status(module),
    queryFn: async () => {
      const res = await apiClient.get<QbStatusResponse>(
        `${endpoints.quickbooks.status}?module=${module}`,
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

export interface QbSyncLogFilters {
  page: number;
  limit?: number;
}

export function useQbSyncLog(module: QbModule, filters: QbSyncLogFilters) {
  return useQuery<QbSyncListResponse>({
    queryKey: queryKeys.quickbooks.syncLog(
      module,
      filters as unknown as Record<string, unknown>,
    ),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('module', module);
      params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      const res = await apiClient.get<QbSyncListResponse>(
        `${endpoints.quickbooks.syncLog}?${params.toString()}`,
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useQbInitiateOauth() {
  const toast = useToast();
  return useMutation({
    mutationFn: () => apiClient.get<QbAuthInitiateResponse>(endpoints.quickbooks.auth),
    onSuccess: (res) => {
      const url = res.data?.auth_url;
      if (url) {
        window.location.assign(url);
      } else {
        toast.error('No QuickBooks auth URL returned.');
      }
    },
    onError: () => {
      toast.error('Failed to start QuickBooks connection.');
    },
  });
}

export function useQbDisconnect() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: () => apiClient.delete(endpoints.quickbooks.disconnect),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.quickbooks.all });
      toast.success('QuickBooks disconnected.');
    },
    onError: () => {
      toast.error('Failed to disconnect.');
    },
  });
}

export interface TriggerSyncInput {
  notes?: string;
}

export function useQbTriggerSync(module: QbModule) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: TriggerSyncInput) =>
      apiClient.post<QbSyncTriggerResponse>(
        `${endpoints.quickbooks.sync}?module=${module}`,
        input.notes ? { notes: input.notes } : undefined,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.quickbooks.all });
      toast.success('Sync started. Refresh in a moment to see progress.');
    },
    onError: () => {
      toast.error('Failed to start sync.');
    },
  });
}

// ---------------------------------------------------------------- SETTINGS

export function useQbSettings(module: QbModule) {
  return useQuery<QbSettingsResponse>({
    queryKey: queryKeys.quickbooks.settings(module),
    queryFn: async () => {
      const res = await apiClient.get<QbSettingsResponse>(
        `${endpoints.quickbooks.settings}?module=${module}`,
      );
      return res.data;
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useUpsertQbSettings(module: QbModule) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (patch: QbSettingsPatch) =>
      apiClient.patch<QbSettingsResponse>(
        `${endpoints.quickbooks.settings}?module=${module}`,
        patch,
      ),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: queryKeys.quickbooks.all });
      const warnings = res.data?.warnings ?? [];
      if (warnings.length > 0) {
        toast.warning(warnings.join(' '));
      } else {
        toast.success('QuickBooks settings saved.');
      }
    },
    onError: () => {
      toast.error('Failed to save QuickBooks settings.');
    },
  });
}

/** Active accounts of the connected QuickBooks company, for the pickers. */
export function useQbAccounts(module: QbModule, enabled: boolean) {
  return useQuery<QbAccountSummary[]>({
    queryKey: queryKeys.quickbooks.accounts(module),
    queryFn: async () => {
      const res = await apiClient.get<{ accounts: QbAccountSummary[] }>(
        `${endpoints.quickbooks.settingsAccounts}?module=${module}`,
      );
      return res.data.accounts;
    },
    enabled,
    staleTime: 60_000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

/** Resolve every configured account against QuickBooks (read-only). */
export function useValidateQbSettings(module: QbModule) {
  const toast = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<QbValidateResult>(
        `${endpoints.quickbooks.settingsValidate}?module=${module}`,
      );
      return res.data;
    },
    onError: () => {
      toast.error('Could not check accounts. Is QuickBooks connected?');
    },
  });
}

export function useResetQbAccounts(module: QbModule) {
  const toast = useToast();
  return useMutation({
    mutationFn: () =>
      apiClient.post(`${endpoints.quickbooks.resetAccounts}?module=${module}`),
    onSuccess: () => {
      toast.success('Account cache reset. Accounts will be re-detected on next sync.');
    },
    onError: () => {
      toast.error('Failed to reset account cache.');
    },
  });
}

// ---------------------------------------------------------------- MAPPINGS

export interface QbMappingsFilters {
  kind?: QbMappingKind;
  q?: string;
  page?: number;
  limit?: number;
}

export function useQbMappings(filters: QbMappingsFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.kind !== undefined) qs.set('kind', filters.kind);
  if (filters.q !== undefined && filters.q !== '') qs.set('q', filters.q);
  if (filters.page !== undefined) qs.set('page', String(filters.page));
  if (filters.limit !== undefined) qs.set('limit', String(filters.limit));
  const filtersForKey: Record<string, unknown> = { ...filters };
  return useQuery<QbMappingsResponse>({
    queryKey: queryKeys.quickbooks.mappings(filtersForKey),
    queryFn: async () => {
      const url = `${endpoints.quickbooks.mappings}${qs.toString() ? `?${qs.toString()}` : ''}`;
      const res = await apiClient.get<QbMappingsResponse>(url);
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useUpsertQbMapping() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (body: QbMappingUpsertBody) =>
      apiClient.post<{ kind: QbMappingKind; customer_name: string; qb_id: string }>(
        endpoints.quickbooks.mappings,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.quickbooks.all });
      toast.success('Mapping saved.');
    },
    onError: () => {
      toast.error('Failed to save mapping.');
    },
  });
}

export function useDeleteQbMapping() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (mapping: QbMappingDto) =>
      apiClient.delete(endpoints.quickbooks.mapping(mapping.enc_id)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.quickbooks.all });
      toast.success('Mapping removed.');
    },
    onError: () => {
      toast.error('Failed to remove mapping.');
    },
  });
}

// ---------------------------------------------------------------- GROUPED SYNC

export function useQbEligibleReceipts(enabled: boolean) {
  return useQuery<QbEligibleResponse>({
    queryKey: queryKeys.quickbooks.eligible(),
    queryFn: async () => {
      const res = await apiClient.get<QbEligibleResponse>(endpoints.quickbooks.syncEligible);
      return res.data;
    },
    enabled,
    staleTime: 15_000,
  });
}

export function useQbSyncRecords(page: number, limit = 25) {
  return useQuery<QbSyncRecordsResponse>({
    queryKey: queryKeys.quickbooks.records({ page, limit }),
    queryFn: async () => {
      const res = await apiClient.get<QbSyncRecordsResponse>(
        `${endpoints.quickbooks.syncRecords}?page=${page}&limit=${limit}`,
      );
      return res.data;
    },
    staleTime: 15_000,
  });
}

/**
 * Sync the chosen receipts as ONE QuickBooks expense. Errors are NOT toasted
 * here: the caller shows the per-group message (and offers the mixed-payment
 * confirmation on `MIXED_PAYMENT_MODES`).
 */
export function useQbGroupSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: QbGroupSyncBody) => {
      const res = await apiClient.post<QbGroupSyncResult>(endpoints.quickbooks.syncGroup, body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.quickbooks.all });
    },
  });
}
